import { useMemo } from "react";
import type { TFunction } from "i18next";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { PackagingOption } from "~/domains/product/types/PackagingOption";
import { buildPackagingOptions, type SalePerPack } from "~/domains/product/utils/buildPackagingOptions";
import { buildPackagingOptionsPhaseout } from "~/domains/product/utils/buildPackagingOptionsPhaseout";
import { useEffectivePackaging } from "~/domains/product/hooks/useEffectivePackaging";

export type PackagingOptionsStatus = "idle" | "loading" | "loaded" | "error";

interface UsePackagingOptionsParams {
  descriptor: ProductDescriptor | null | undefined;
  priceReference: string;
  salePerPack: SalePerPack;
  restrictionPackCode?: string;
  t: TFunction;
}

interface UsePackagingOptionsResult {
  options: PackagingOption[];
  status: PackagingOptionsStatus;
}

export function usePackagingOptions({
  descriptor,
  priceReference,
  salePerPack,
  restrictionPackCode,
  t,
}: UsePackagingOptionsParams): UsePackagingOptionsResult {
  const isPhaseoutEnabled = isPhaseOut2Enabled();
  const effectivePackaging = useEffectivePackaging({
    descriptor,
    priceReference,
    restrictionPackCode,
  });

  const options = useMemo(() => {
    if (!descriptor || effectivePackaging.status !== "loaded" || !descriptor.availability) {
      return [] as PackagingOption[];
    }

    const activeEntries = effectivePackaging.entries.map((entry) => entry.rawPack);

    if (!isPhaseoutEnabled) {
      return buildPackagingOptions(activeEntries, descriptor, salePerPack, t);
    }

    return buildPackagingOptionsPhaseout(activeEntries, descriptor, salePerPack, t, effectivePackaging.predecessors);
  }, [
    descriptor,
    effectivePackaging.entries,
    effectivePackaging.predecessors,
    effectivePackaging.status,
    isPhaseoutEnabled,
    salePerPack,
    t,
  ]);

  return {
    options,
    status: effectivePackaging.status,
  };
}
